import axiosInstance from '../../api/axiosInstance';
import { getSafeRedirect } from '../../utils/security';
import { toast } from 'react-toastify';
import { setInitialService } from './dashboard';
import AuthApi from '../../api/auth';
import config from '../../config';
import { clearAllAuth, setLoggedIn, setAdminUser } from '../../utils/auth';



export const AUTH_FAILURE = 'AUTH_FAILURE';
export const LOGIN_REQUEST = 'LOGIN_REQUEST';
export const LOGIN_SUCCESS = 'LOGIN_SUCCESS';
export const LOGOUT_REQUEST = 'LOGOUT_REQUEST';
export const LOGOUT_SUCCESS = 'LOGOUT_SUCCESS';
export const RESET_REQUEST = 'RESET_REQUEST';
export const RESET_SUCCESS = 'RESET_SUCCESS';
export const PASSWORD_RESET_EMAIL_REQUEST = 'PASSWORD_RESET_EMAIL_REQUEST';
export const PASSWORD_RESET_EMAIL_SUCCESS = 'PASSWORD_RESET_EMAIL_SUCCESS';
export const AUTH_INIT_SUCCESS = 'AUTH_INIT_SUCCESS';
export const AUTH_INIT_ERROR = 'AUTH_INIT_ERROR';
export const SERVICE_SWITCH_SUCCESS = 'SERVICE_SWITCH_SUCCESS';

export function authError(payload) {
  return {
    type: AUTH_FAILURE,
    payload,
  };
}
// Action creator for service switch success
export function serviceSwitchSuccess(currentUser) {
  return {
    type: SERVICE_SWITCH_SUCCESS,
    payload: { currentUser }
  };
}

// Global flag to prevent multiple doInit calls during redirect
let isInitializing = false;

export function doInit() {
  return async (dispatch) => {
    // Check if we're currently redirecting (to prevent loop during page reload)
    const isRedirecting = sessionStorage.getItem('_redirecting');
    if (isRedirecting === 'true') {
      return;
    }

    // skip session check on login/auth pages — no need to call /me
    const currentPath = window.location.pathname;
    const isAuthPage = currentPath === '/login' || 
                       currentPath === '/forgot' || 
                       currentPath === '/password-reset';
    
    if (isAuthPage) {
      dispatch({
        type: AUTH_INIT_SUCCESS,
        payload: { currentUser: null },
      });
      return;
    }

    // Prevent multiple simultaneous calls
    if (isInitializing) {
      return;
    }

    isInitializing = true;
    let currentUser = null;

    try {
      // We no longer use tokens in sessionStorage/localStorage.
      // Authentication is now handled entirely via HTTPOnly cookies.
      // Attempt to fetch user details to verify the session cookie
      try {
        const meResponse = await AuthApi.Me();
        const { user, active_company, active_service, services } = meResponse.data || {};

        if (user) {
          setLoggedIn(true);

          // Build normalizedUser first so it's available in all branches below
          const normalizedUser = {
            ...(user || {}),
            active_company,
            active_service,
            companyId: active_company?.company_id,
            companyName: active_company?.company__company_name || active_company?.company_name,
            mobile: active_company?.company__mobile_number || active_company?.mobile || user?.mobile,
            hideStatus: active_company?.hide_status ?? false,
            services: Array.isArray(services) ? services : [],
            selectedService: active_service?.service__sname || active_service?.service_id
          };

          // Detect system admin from the API response so new tabs (fresh sessionStorage)
          // are also caught — not just sessions that went through loginUser()
          const isSystemAdmin = user.role === 'system_admin' || user.is_staff || user.is_superuser;
          if (isSystemAdmin) {
            sessionStorage.setItem('admin_mode', 'admin');
            setAdminUser(user);
          }

          const adminMode = sessionStorage.getItem('admin_mode');
          if (adminMode === 'admin') {
            const currentPath = window.location.pathname;
            if (!currentPath.startsWith('/admin')) {
              sessionStorage.setItem('_redirecting', 'true');
              isInitializing = false;

              setTimeout(() => {
                sessionStorage.removeItem('_redirecting');
              }, 2000);

              dispatch({
                type: AUTH_INIT_SUCCESS,
                payload: { currentUser: normalizedUser },
              });

              window.location.replace('/admin');
              return;
            }
          }

          currentUser = normalizedUser;

          if (active_service?.service__sname) {
            dispatch(setInitialService(active_service.service__sname.toLowerCase().replace(/\s+/g, '_')));
          }
        }
      } catch (meError) {
        // If /auth/me/ fails, the session is likely invalid or missing
        const status = meError?.response?.status;
        if (status === 401 || status === 403) {
          // Session is invalid, ignore and user remains unauthenticated
          if (process.env.NODE_ENV !== 'production') {
            console.log('No active session found on init.');
          }
        } else {
          // For other errors (network, etc.), log it
          console.warn('Network error or server down during session check:', meError);
        }
      }
      dispatch({
        type: AUTH_INIT_SUCCESS,
        payload: {
          currentUser,
        },
      });
      isInitializing = false;
    } catch (error) {
      isInitializing = false;

      // Enhanced error handling with user-friendly messages
      const errorMessage = error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        'Authentication failed';

      // Only redirect to login if we're not already on a protected route
      const currentPath = window.location.pathname;
      const isProtectedRoute = currentPath.includes('/dashboard') ||
        currentPath.includes('/analytics');

      if (isProtectedRoute) {
        // Clear local credentials flags
        clearAllAuth();

        dispatch({
          type: AUTH_INIT_ERROR,
          payload: { ...error, userMessage: errorMessage },
        });

        // Redirect to login on error
        window.location.href = '/login';
      } else {
        // Clear local credentials flags
        clearAllAuth();

        dispatch({
          type: AUTH_INIT_ERROR,
          payload: { ...error, userMessage: errorMessage },
        });
      }
    }
  }
}

export function logoutUser(navigate) {
  return async (dispatch) => {
    dispatch({
      type: LOGOUT_REQUEST,
    });

    try {
      // Call Django logout endpoint using AuthApi
      await AuthApi.Logout();
    } catch (error) {
      // Continue with logout even if API call fails
    }

    // Clear local state
    clearAllAuth();

    dispatch({
      type: LOGOUT_SUCCESS,
    });

    // Use React Router navigation instead of window.location.href
    if (navigate) {
      navigate('/login');
    } else {
      // Fallback to window.location if navigate is not available
      window.location.href = '/login';
    }
  };
}

export function loginUser(creds, navigate) {
  return async (dispatch) => {
    dispatch({ type: LOGIN_REQUEST });

    if (!creds) {
      dispatch(authError('Login credentials are required'));
      return;
    }

    if (creds.social) {
      const baseUrl = config.django.baseURL.replace('/api/users/', '').replace('/api/users', '');
      const redirectUrl = getSafeRedirect(config.redirectURL || 'http://localhost:3000');
      window.location.href = baseUrl + "/auth/signin/" + creds.social + '?app=' + redirectUrl;
      return;
    }

    if (!creds.email || !creds.password || creds.email.length === 0 || creds.password.length === 0) {
      dispatch(authError('Email and password are required'));
      return;
    }

    try {
      // Use AuthApi.Login which sets HTTPOnly cookies on success
      const res = await AuthApi.Login({ email: creds.email, password: creds.password });
      const { user, mode } = res.data || {};

      // Authentication is now cookie-based; success is determined by the response status.
      setLoggedIn(true);

      if (mode === 'admin') {
        sessionStorage.setItem('admin_mode', 'admin');
        sessionStorage.setItem('admin_user', JSON.stringify(user || {}));

        dispatch({
          type: LOGIN_SUCCESS,
          payload: { currentUser: user },
        });

        if (navigate) {
          navigate('/admin', { replace: true });
        } else {
          window.location.href = '/admin';
        }
        return;
      }

      // Company user: fetch full context from /me so the dashboard has
      // company, services, and active_service immediately available.
      let fullUser = { ...(user || {}) };
      try {
        const meResponse = await AuthApi.Me();
        const { user: meUser, active_company, active_service, services } = meResponse.data || {};

        fullUser = {
          ...(meUser || user || {}),
          active_company,
          active_service,
          companyId: active_company?.company_id,
          companyName: active_company?.company__company_name || active_company?.company_name,
          mobile: active_company?.company__mobile_number || active_company?.mobile || meUser?.mobile,
          hideStatus: active_company?.hide_status ?? false,
          services: Array.isArray(services) ? services : [],
          selectedService: active_service?.service__sname || active_service?.service_id,
        };

        if (active_service?.service__sname) {
          dispatch(setInitialService(active_service.service__sname.toLowerCase().replace(/\s+/g, '_')));
        }
      } catch (meError) {
        // Fallback: use whatever the login response gave us
      }

      dispatch({
        type: LOGIN_SUCCESS,
        payload: { currentUser: fullUser },
      });

      // Also dispatch AUTH_INIT_SUCCESS so loadingInit becomes false
      dispatch({
        type: AUTH_INIT_SUCCESS,
        payload: { currentUser: fullUser },
      });

      if (navigate) {
        navigate('/dashboard');
      } else {
        window.location.href = '/dashboard';
      }
    } catch (err) {
      const errorMessage =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.response?.data?.non_field_errors?.[0] ||
        err?.message ||
        'Login failed. Please check your credentials and try again.';
      dispatch(authError(errorMessage));
    }
  };
}

export function verifyEmail(token) {
  return (dispatch) => {
    axiosInstance.put("/auth/verify-email", { token }).then(verified => {
      if (verified) {
        toast.success("Your email was verified");
      }
    }).catch(err => {
      const message = err?.response?.data?.detail || err?.response?.data || err?.message || 'Email verification failed';
      toast.error(message);
    });
  }
}

export function resetPassword(token, password) {
  return (dispatch) => {
    dispatch({
      type: RESET_REQUEST,
    });
    axiosInstance.put("/auth/password-reset", { token, password }).then(res => {
      dispatch({
        type: RESET_SUCCESS,
      });
      toast.success("Password has been updated");
    }).catch(err => {
      const message = err?.response?.data?.detail || err?.response?.data || err?.message || 'Password reset failed';
      dispatch(authError(message));
    })
  }
}

export function sendPasswordResetEmail(email) {
  return (dispatch) => {
    dispatch({
      type: PASSWORD_RESET_EMAIL_REQUEST,
    });
    axiosInstance.post("/auth/send-password-reset-email", { email }).then(res => {
      dispatch({
        type: PASSWORD_RESET_EMAIL_SUCCESS,
      });
      toast.success("Email with resetting instructions has been sent");
    }).catch(err => {
      const message = err?.response?.data?.detail || err?.response?.data || err?.message || 'Failed to send reset email';
      dispatch(authError(message));
    })
  }
}

